import { useEffect, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { t } from '@/i18n'

/**
 * Rows on a page.
 *
 * Twenty, so a page is read rather than scrolled through: fifty still ran past the bottom of the
 * screen, which put the sections under the table out of sight again and defeated the point of
 * paging it at all.
 */
const PAGE = 20

interface RowWindowProps<T> {
  rows: readonly T[]
  children: (row: T, index: number) => ReactNode
  /**
   * Set when the rows are table rows: the footer then spans the table instead of being crushed
   * into the first column. Left off for a list of <li>.
   */
  colSpan?: number
}

/**
 * Pages through a list that arrived whole.
 *
 * Not every long list is a paged endpoint. A report has to come back in one piece, because the
 * totals under it are computed from all of it — but a thousand rows painted at once is tens of
 * thousands of table cells, which locks up a phone at the counter for seconds.
 *
 * <strong>A page at a time rather than a growing list.</strong> Appending rows was the first
 * attempt and it made the table taller every press, so the sections underneath — the category
 * split, the payment mix — slid further down the page and became something to hunt for. A fixed
 * fifty keeps everything below the table exactly where it was.
 *
 * It always says which rows are on screen and how many there are, for the same reason the paged
 * endpoints do: a table that stops looks exactly like a shop that had that many. It matters more
 * here, because the total underneath covers every row — so the figures visible genuinely do not add
 * up to the line beneath them, and only this explains it.
 */
export function RowWindow<T>({ rows, children, colSpan }: RowWindowProps<T>) {
  const [page, setPage] = useState(0)

  // A fresh result starts at the first page rather than keeping the last one's position — which
  // could be past the end of a shorter report, leaving an empty table and no way to tell why.
  useEffect(() => setPage(0), [rows])

  const pages = Math.max(1, Math.ceil(rows.length / PAGE))
  const current = Math.min(page, pages - 1)
  const from = current * PAGE
  const visible = rows.slice(from, from + PAGE)

  const footer = rows.length > PAGE && (
    <div className="flex flex-wrap items-center justify-center gap-3 py-3">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setPage(current - 1)}
        disabled={current === 0}
        aria-label={t('Previous page')}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <span className="tabular text-xs text-[var(--text-secondary)]">
        {t('{from}–{to} of {total}', {
          from: (from + 1).toLocaleString(),
          to: (from + visible.length).toLocaleString(),
          total: rows.length.toLocaleString(),
        })}
      </span>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => setPage(current + 1)}
        disabled={current >= pages - 1}
        aria-label={t('Next page')}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )

  if (colSpan !== undefined) {
    return (
      <>
        {visible.map((row, index) => children(row, from + index))}
        {footer && (
          <tr>
            <td colSpan={colSpan} className="px-3 text-center">
              {footer}
            </td>
          </tr>
        )}
      </>
    )
  }

  return (
    <>
      {visible.map((row, index) => children(row, from + index))}
      {footer && <li>{footer}</li>}
    </>
  )
}
